import { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mic, Square, Loader2 } from "lucide-react";

export const VoiceSearchButton = ({ onResult }) => {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime });
        if (blob.size < 1500) return;
        setBusy(true);
        try {
          const form = new FormData();
          form.append("file", blob, mime.includes("webm") ? "voice.webm" : "voice.mp4");
          const { data } = await api.post("/ai/voice-search", form, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 60000,
          });
          onResult(data.query);
          try {
            const res = await api.post("/tts/speak", { text: `Showing results for ${data.query}` }, { responseType: "blob", timeout: 30000 });
            const url = URL.createObjectURL(res.data);
            const audio = new Audio(url);
            audio.onended = () => URL.revokeObjectURL(url);
            audio.play().catch(() => {});
          } catch { /* silent — confirmation audio is best-effort */ }
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || "Voice search failed");
        } finally {
          setBusy(false);
        }
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
    } catch {
      toast.error("Allow microphone access to search by voice");
    }
  };

  const stop = () => {
    recRef.current?.stop();
    setRecording(false);
  };

  return (
    <button
      type="button"
      onClick={recording ? stop : start}
      disabled={busy}
      data-testid="voice-search-btn"
      aria-label={recording ? "Stop and search" : "Search by voice"}
      className={`p-1.5 rounded-full transition-colors ${
        recording ? "text-[#FF3B30] animate-pulse bg-[#FF3B30]/10" : "text-neutral-500 hover:text-white"
      }`}
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : recording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
    </button>
  );
};
